import { type MouseEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Circle } from 'lucide-react';

import { cn } from '@/lib/utils';
import { useUpdateTask } from '@/features/patient-record/hooks/useTasksMutations';

interface TaskCompleteButtonProps {
  readonly guid: string;
  readonly taskId: string;
  readonly className?: string;
}

/**
 * Inline circle-checkbox that flips a TODO task to DONE without
 * opening the full edit dialog (PTV2-1202). Sits on the left edge of
 * each open task row in the side panel and header preview. Stops
 * propagation so the surrounding row-click (which opens the edit
 * dialog) doesn't also fire.
 */
export function TaskCompleteButton({
  guid,
  taskId,
  className,
}: TaskCompleteButtonProps): ReactElement {
  const { t } = useTranslation();
  const update = useUpdateTask(guid);

  const handleClick = (event: MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation();
    if (update.isPending) return;
    update.mutate({ taskId, input: { status: 'DONE' } });
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={update.isPending}
      aria-label={t('patient.tasks.markCompleteAriaLabel')}
      className={cn(
        'group relative inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors',
        'hover:text-success focus-visible:text-success focus-visible:outline-hidden',
        'disabled:cursor-progress disabled:opacity-60',
        className,
      )}
    >
      <Circle className="h-4 w-4" aria-hidden="true" />
      <Check
        className="absolute h-2.5 w-2.5 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
        aria-hidden="true"
      />
    </button>
  );
}
